<template>

  <ui-card name="overview.usage_stats" :is-loading="isLoading" :error="error" padded>
    <div class="columns">
      <div class="column">
        <stat-content v-if="sdkInstallsContent" :content="sdkInstallsContent" />
        <stat-blokk v-else-if="isSdkInstallsLoading" />
        <stat-no-content v-else :content-type="'sdkInstalls'" />
      </div>
      <div class="column">
        <stat-content v-if="optedInContent" :content="optedInContent" />
        <stat-blokk v-else-if="isOptedInLoading" />
        <stat-no-content v-else :content-type="'optedIn'" />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <stat-content v-if="usersTrackingContent " :content="usersTrackingContent" />
        <stat-blokk v-else-if="isUsersTrackingLoading" />
        <stat-no-content v-else :content-type="'usersTracking'" />
      </div>
      <div class="column">
        <stat-content v-if="revokedLocationContent" :content="revokedLocationContent" />
        <stat-blokk v-else-if="isRevokedLocationLoading" />
        <stat-no-content v-else :content-type="'revokedLocation'" />
      </div>
    </div>

  </ui-card>

</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { has, assignIn, zipObject, flatten, first, last } from 'lodash';
import { map, sortBy, flow } from 'lodash/fp';
import { parse, distanceInWords } from 'date-fns';
import StatContent from './StatContent';
import StatNoContent from './StatNoContent';
import StatBlokk from './StatBlokk';
import {
  makeQuery,
  fetchSdkInstallsData,
  fetchUsersTrackingData,
  fetchOptedInData,
  fetchRevokedLocationData,
} from 'store/queries/usage';

import { card } from 'views/mixins';

const parent = card('filter-dates');

export default {
  extends: parent,

  components: {
    StatContent,
    StatNoContent,
    StatBlokk,
  },

  data() {
    return {
      // data
      sets: [],

      // content
      sdkInstallsContent: null,
      usersTrackingContent: null,
      optedInContent: null,
      revokedLocationContent: null,

      // loading
      isSdkInstallsLoading: false,
      isUsersTrackingLoading: false,
      isOptedInLoading: false,
      isRevokedLocationLoading: false,

      // none
      noneStateContent: null,
      defaultOptions: {
        info: null,
        hasDeclined: false,
        hasWarning: false,
      },
    };
  },

  methods: {
    load() {
      this.setLoading(true);
      parent.methods.load.call(this);
    },

    makeQuery(params) {
      const query = makeQuery(params);
      // debugger
      return query;
    },

    parseData(...sets) {
      // loading
      this.setLoading(false);

      // original data
      this.sdkInstallsContent = this.getSDKInstallsContent(sets[0], sets[1]);
      this.usersTrackingContent = this.getUsersTrackingContent(
        sets[2],
        sets[3]
      );
      this.optedInContent = this.getOptedInContent(sets[4]);
      this.revokedLocationContent = this.getRevokedLocationContent(sets[5]);

      // hash
      return {
        sdk: this.sdkInstallsContent,
        users: this.usersTrackingContent,
        optedIn: this.optedInContent,
        revokedLocation: this.revokedLocationContent,
      };
    },

    setIndividualData(type) {
      switch (type) {
        case 'sdkInstalls':
          this.setSdkInstallsData();
          break;
        case 'usersTracking':
          this.setUsersTrackingData();
          break;
        case 'optedIn':
          this.setOptedInData();
          break;
        case 'revokedLocation':
          this.setRevokedLocationData();
          break;
        default:
          return;
      }
    },

    setSdkInstallsData(appId = this.appId, timeRange = this.timeRange) {
      this.isSdkInstallsLoading = true;

      fetchSdkInstallsData(appId, timeRange).then(
        res => {
          this.isSdkInstallsLoading = false;

          if (!has(res, 'body.sets') || res.body.sets.length === 0) {
            return;
          }

          const sets = res.body.sets;

          this.sdkInstallsContent = this.getSDKInstallsContent(
            sets[0],
            sets[1]
          );
        },
        err => {
          this.isSdkInstallsLoading = false;
        }
      );
    },

    setUsersTrackingData(appId = this.appId, timeRange = this.timeRange) {
      this.isUsersTrackingLoading = true;

      fetchUsersTrackingData(appId, timeRange).then(
        res => {
          this.isUsersTrackingLoading = false;

          if (!has(res, 'body.sets') || res.body.sets.length === 0) {
            return;
          }

          const sets = res.body.sets;

          this.usersTrackingContent = this.getUsersTrackingContent(
            sets[0],
            sets[1]
          );
        },
        err => {
          this.isUsersTrackingLoading = false;
        }
      );
    },

    setOptedInData(appId = this.appId, timeRange = this.timeRange) {
      this.isOptedInLoading = true;

      fetchOptedInData(appId, timeRange).then(
        res => {
          this.isOptedInLoading = false;

          if (!has(res, 'body.sets') || res.body.sets.length === 0) {
            return;
          }

          const sets = res.body.sets;

          this.optedInContent = this.getOptedInContent();
        },
        err => {
          this.isOptedInLoading = false;
        }
      );
    },

    setRevokedLocationData(appId = this.appId, timeRange = this.timeRange) {
      this.isRevokedLocationLoading = true;

      fetchRevokedLocationData(appId, timeRange).then(
        res => {
          this.isRevokedLocationLoading = false;

          if (!has(res, 'body.sets') || res.body.sets.length === 0) {
            return;
          }

          const sets = res.body.sets;

          this.revokedLocationContent = this.getRevokedLocationContent();
        },
        err => {
          this.isRevokedLocationLoading = false;
        }
      );
    },

    getSDKInstallsContent(nrUsers, nrUsersTotal) {
      if (
        !has(nrUsers, 'rows[0][0]') ||
        !has(nrUsersTotal, 'rows') ||
        nrUsersTotal.rows.length <= 1
      ) {
        return null;
      }

      nrUsers = this.rowsToObject(nrUsers.rows, nrUsers.fields);

      const title = nrUsers[0].value;
      const data = this.getDataObject(nrUsersTotal.rows, nrUsersTotal.fields);
      const extent = this.getExtent(data);
      const status = this.getStatus(extent[0], extent[1], this.timeRange);
      const options = this.getOptions(extent[0], extent[1]);

      return this.createContent(
        title,
        this.$t('overview.usage_stats.sdk_installs_content.title'),
        status,
        data,
        options
      );
    },

    getUsersTrackingContent(nrUsers, nrUsersTotal) {
      if (
        !has(nrUsers, 'rows[0][0]') ||
        !has(nrUsersTotal, 'rows') ||
        nrUsersTotal.rows.length <= 1
      ) {
        return null;
      }

      nrUsers = this.rowsToObject(nrUsers.rows, nrUsers.fields);

      const title = nrUsers[0].value;
      const data = this.getDataObject(nrUsersTotal.rows, nrUsersTotal.fields);
      const extent = this.getExtent(data);
      const status = this.getStatus(extent[0], extent[1], this.timeRange);
      const options = this.getOptions(extent[0], extent[1]);

      return this.createContent(
        title,
        this.$t('overview.usage_stats.users_tracking_content.title'),
        status,
        data,
        options
      );
    },

    getOptedInContent(set) {
      if (!has(set, 'rows') || set.rows.length <= 1) {
        return null;
      }

      const data = this.getDataObject(set.rows, set.fields);
      const extent = this.getExtent(data);
      const status = this.getStatus(extent[0], extent[1], this.timeRange);
      const options = this.getOptions(extent[0], extent[1]);

      return this.createContent(
        '72%',
        this.$t('overview.usage_stats.opted_in_content.title'),
        status,
        data,
        options
      );
    },

    getRevokedLocationContent(set) {
      if (!has(set, 'rows') || set.rows.length <= 1) {
        return null;
      }

      const data = this.getDataObject(set.rows, set.fields);
      const extent = this.getExtent(data);
      const status = this.getStatus(extent[0], extent[1], this.timeRange);
      const options = this.getOptions(extent[0], extent[1]);

      return this.createContent(
        '3.1%',
        this.$t('overview.usage_stats.revoked_location_content.title'),
        status,
        data,
        options
      );
    },

    createContent(
      title,
      subtitle,
      status,
      data,
      options = this.defaultOptions
    ) {
      return { title, subtitle, status, data, options };
    },

    rowsToObject(rows, fields) {
      fields = map(field => field.custom_id)(fields);
      rows = map(row => zipObject(fields, row))(rows);

      return rows;
    },

    getDataObject(rows, fields) {
      const rowsObject = this.rowsToObject(rows, fields);

      const data = flow(
        map(row => assignIn({}, row, { time: parse(row.time) })),
        sortBy('time')
      )(rowsObject);

      return data;
    },

    getExtent(data) {
      const first = data[0].value;
      const last = data[data.length - 1].value;

      return [first, last];
    },

    getStatus(fromValue, toValue, timeRange) {
      const value = ((toValue / fromValue - 1) * 100).toFixed(2);
      const type = this.$t(
        value > 0
          ? 'overview.usage_stats.growth'
          : 'overview.usage_stats.decline'
      );
      const period = distanceInWords(this.timeRange.from, this.timeRange.to);

      return {
        path: 'overview.usage_stats.status',
        args: {
          value: Math.abs(value),
          type: type,
          period: period,
        },
      };
    },

    getOptions(fromValue, toValue, info = null) {
      const hasDeclined = toValue < fromValue;
      const hasWarning = toValue / fromValue - 1 < -0.25 ? true : false;

      return assignIn({}, this.defaultOptions, {
        info: info,
        hasDeclined: hasDeclined,
        hasWarning: hasWarning,
      });
    },

    setLoading(isLoading) {
      this.isSdkInstallsLoading = isLoading;
      this.isUsersTrackingLoading = isLoading;
      this.isOptedInLoading = isLoading;
      this.isRevokedLocationLoading = isLoading;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>